import React, { useState } from "react";
import ConfirmPopUp from "./ConfirmPopUp";
import McqDisplayMcq from "./McqDisplayMcq";
import AttachDisplay from "./AttachDisplay";
import {
  Box,
  Paper,
  Typography,
  Button,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { ListChecks, Award } from "lucide-react";

const ACCENT = "#f77f00";
const ACCENT_SOFT = "#fff4e5";
const BORDER = "#e6e9ef";
const TEXT_PRIMARY = "#1f2733";
const TEXT_SECONDARY = "#5b6472";
const BG_SOFT = "#f8f9fb";

export default function PublishPreviewQB({
  setPreview,
  displayQuestions,
  details,
  type,
  selectedBox,
}) {
  const [publishQb, setPubishQb] = useState(false);

  const totalMarks = (displayQuestions || []).reduce(
    (acc, q) => acc + (Number(q?.correct_score) || 0),
    0,
  );

  return (
    <>
      {!publishQb && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1300,
            p: 2,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 3,
              borderRadius: "16px",
              background: "#fff",
              border: `1px solid ${BORDER}`,
              position: "relative",
              width: { xs: "96%", sm: "92%", md: "88%", lg: "1100px" },
              maxWidth: "1100px",
              maxHeight: "95vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <IconButton
              onClick={() => setPreview(false)}
              size="small"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                color: TEXT_SECONDARY,
                background: BG_SOFT,
                border: `1px solid ${BORDER}`,
                "&:hover": { background: ACCENT_SOFT, color: ACCENT },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            {/* Header */}
            <Box
              sx={{
                mb: 2,
                pr: 5,
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: TEXT_PRIMARY,
                    letterSpacing: "-0.2px",
                  }}
                >
                  {details?.name || "Review & Publish"}
                </Typography>
                <Typography sx={{ fontSize: "12.5px", color: TEXT_SECONDARY }}>
                  Review all questions and answers before publishing
                </Typography>
              </Box>
              {/* 🔥 REMOVED: AI Evaluation Chip */}
            </Box>

            {/* Stat Cards */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 1.5,
                mb: 2.5,
              }}
            >
              {/* Total Questions */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${BORDER}`,
                  background: BG_SOFT,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    background: ACCENT_SOFT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ListChecks size={18} color={ACCENT} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: TEXT_SECONDARY,
                      letterSpacing: "0.4px",
                    }}
                  >
                    TOTAL QUESTIONS
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: TEXT_PRIMARY,
                      lineHeight: 1.1,
                    }}
                  >
                    {displayQuestions?.length || 0}
                  </Typography>
                </Box>
              </Paper>

              {/* Total Marks */}
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: "12px",
                  border: `1px solid ${BORDER}`,
                  background: BG_SOFT,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: "10px",
                    background: ACCENT_SOFT,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Award size={18} color={ACCENT} />
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: "11px",
                      fontWeight: 600,
                      color: TEXT_SECONDARY,
                      letterSpacing: "0.4px",
                    }}
                  >
                    TOTAL MARKS
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 700,
                      color: TEXT_PRIMARY,
                      lineHeight: 1.1,
                    }}
                  >
                    {totalMarks}
                  </Typography>
                </Box>
              </Paper>
            </Box>

            <Divider sx={{ borderColor: BORDER, mb: 2 }} />

            <Box
              sx={{
                overflowY: "auto",
                pr: 1,
                flex: 1,
                minHeight: 0,
                "&::-webkit-scrollbar": { width: "8px" },
                "&::-webkit-scrollbar-track": {
                  background: BG_SOFT,
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: ACCENT,
                  borderRadius: "10px",
                },
                "&::-webkit-scrollbar-thumb:hover": { background: "#e56f00" },
                scrollbarWidth: "thin",
                scrollbarColor: `${ACCENT} ${BG_SOFT}`,
              }}
            >
              {displayQuestions?.length > 0 &&
                displayQuestions?.map((question, index) => {
                  const isMcq = question?.question_type === "false";

                  return isMcq ? (
                    <McqDisplayMcq
                      key={index}
                      nextQuestion={index}
                      question={question}
                    />
                  ) : (
                    <AttachDisplay
                      key={index}
                      nextQuestion={index}
                      question={question}
                      selectedBox={selectedBox}
                    />
                  );
                })}
            </Box>

            <Box
              sx={{
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${BORDER}`,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                variant="contained"
                startIcon={<RocketLaunchIcon />}
                onClick={() => setPubishQb(true)}
                sx={{
                  background: ACCENT,
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "13.5px",
                  px: 3,
                  py: 0.9,
                  boxShadow: "0 4px 12px rgba(247,127,0,0.25)",
                  "&:hover": {
                    background: "#e56f00",
                    boxShadow: "0 6px 16px rgba(247,127,0,0.35)",
                  },
                }}
              >
                Publish
              </Button>
            </Box>
          </Paper>
        </Box>
      )}

      {publishQb && type === "create" && (
        <ConfirmPopUp
          close={setPubishQb}
          questions={displayQuestions}
          basicDetails={details}
          type={"create"}
        />
      )}
      {publishQb && type !== "create" && (
        <ConfirmPopUp
          close={setPubishQb}
          questions={displayQuestions}
          basicDetails={details}
        />
      )}
    </>
  );
}
